class DarkrimLogoReview extends HTMLElement {
  connectedCallback() {
    this.slideshow = this.querySelector('slideshow-component');
    this.slides = Array.from(this.querySelectorAll('[data-darkrim-logo-slide]'));
    this.triggers = Array.from(this.querySelectorAll('[data-darkrim-logo-trigger]'));
    this.reviews = Array.from(this.querySelectorAll('[data-darkrim-review]'));
    this.total = Number(this.dataset.itemCount) || this.slides.length;
    this.currentIndex = this.normalizeIndex(Number(this.dataset.initialIndex) || 0);
    this.currentPhysicalIndex = this.total + this.currentIndex;
    this.autoplayInterval = Number(this.dataset.autoplayInterval) || 0;
    this.resetTimer = undefined;
    this.autoplayTimer = undefined;
    this.interactionActive = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.addEventListener('slideshow:select', this.handleSlideSelect);
    this.addEventListener('click', this.handleTriggerClick);
    this.addEventListener('shopify:block:select', this.handleBlockSelect);
    this.addEventListener('pointerdown', this.handlePointerDown);
    this.addEventListener('pointerup', this.handlePointerEnd);
    this.addEventListener('pointercancel', this.handlePointerEnd);
    this.reducedMotion.addEventListener?.('change', this.handleReducedMotionChange);

    this.update(this.currentIndex);
    this.scheduleInitialPosition();
    this.applyReducedMotionPreference();
  }

  disconnectedCallback() {
    this.removeEventListener('slideshow:select', this.handleSlideSelect);
    this.removeEventListener('click', this.handleTriggerClick);
    this.removeEventListener('shopify:block:select', this.handleBlockSelect);
    this.removeEventListener('pointerdown', this.handlePointerDown);
    this.removeEventListener('pointerup', this.handlePointerEnd);
    this.removeEventListener('pointercancel', this.handlePointerEnd);
    this.reducedMotion?.removeEventListener?.('change', this.handleReducedMotionChange);
    window.clearTimeout(this.resetTimer);
    window.clearInterval(this.autoplayTimer);
    window.cancelAnimationFrame(this.initialPositionFrame);
    window.cancelAnimationFrame(this.initialPositionInnerFrame);
  }

  handleSlideSelect = (event) => {
    if (!this.slideshow?.contains(event.detail.slide)) return;

    const logicalIndex = Number(event.detail.slide.dataset.itemIndex);
    this.currentPhysicalIndex = event.detail.index;
    this.update(logicalIndex);
    this.scheduleMiddleCopyReset(event.detail.index, logicalIndex);
  };

  // Visible logos can be selected directly without adding duplicate carousel controls.
  handleTriggerClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-darkrim-logo-trigger]');
    if (!trigger || !this.contains(trigger)) return;

    const index = Number(trigger.dataset.slideIndex);
    if (Number.isInteger(index)) this.slideshow?.select(index, event);
  };

  // Keep the active review synchronized with the block selected in Shopify's Theme Editor.
  handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const slide = event.target.closest('[data-darkrim-logo-slide]');
    const index = this.slides.indexOf(slide);

    if (index >= 0) this.slideshow?.select(index, undefined, { animate: false });
  };

  handleReducedMotionChange = () => this.applyReducedMotionPreference();

  handlePointerDown = () => {
    this.interactionActive = true;
  };

  handlePointerEnd = () => {
    this.interactionActive = false;
  };

  applyReducedMotionPreference() {
    if (this.reducedMotion.matches) {
      this.stopAutoplay();
      return;
    }

    this.startAutoplay();
  }

  scheduleInitialPosition() {
    this.initialPositionFrame = window.requestAnimationFrame(() => {
      this.initialPositionInnerFrame = window.requestAnimationFrame(() => {
        if (!this.slideshow) return;

        // The theme component records a non-zero initial index before its first scroll.
        // Resetting the state first lets its public select method perform the required scroll.
        this.slideshow.current = 0;
        this.slideshow.select(this.currentPhysicalIndex, undefined, { animate: false });
      });
    });
  }

  startAutoplay() {
    if (!this.autoplayInterval || this.autoplayTimer) return;

    this.autoplayTimer = window.setInterval(() => {
      if (
        this.reducedMotion.matches ||
        this.interactionActive ||
        this.matches(':hover') ||
        this.matches(':focus-within') ||
        document.hidden
      ) {
        return;
      }

      this.slideshow?.select(this.currentPhysicalIndex + 1);
    }, this.autoplayInterval);
  }

  stopAutoplay() {
    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = undefined;
  }

  normalizeIndex(index) {
    if (!this.total) return 0;
    return ((index % this.total) + this.total) % this.total;
  }

  // Equivalent copies before and after the primary set keep both sides populated.
  scheduleMiddleCopyReset(physicalIndex, logicalIndex) {
    window.clearTimeout(this.resetTimer);

    if (physicalIndex >= this.total && physicalIndex < this.total * 2) return;

    this.resetTimer = window.setTimeout(() => {
      this.slideshow?.select(this.total + this.normalizeIndex(logicalIndex), undefined, { animate: false });
    }, 420);
  }

  update(index) {
    if (!this.total) return;

    this.currentIndex = this.normalizeIndex(index);

    this.slides.forEach((slide) => {
      const itemIndex = Number(slide.dataset.itemIndex);

      slide.toggleAttribute('data-active', itemIndex === this.currentIndex);
    });

    this.triggers.forEach((trigger) => {
      const itemIndex = Number(trigger.dataset.itemIndex);
      trigger.setAttribute('aria-pressed', String(itemIndex === this.currentIndex));
    });

    this.reviews.forEach((review, itemIndex) => {
      review.setAttribute('aria-hidden', String(itemIndex !== this.currentIndex));
    });
  }
}

if (!customElements.get('darkrim-logo-review')) {
  customElements.define('darkrim-logo-review', DarkrimLogoReview);
}
